<template>
    <div class="main">
        <c-panel title="FICHA DE RISCO DO ALUNO" titleIcon="fas fa-user-shield">
            <el-alert type="warning" :closable="false" class="u-mb-15"
                title="Indicador de risco, não determinação sobre o aluno."
                :description="ficha ? ficha.aviso_metodologico : 'Leia sempre os fatores antes de qualquer decisão.'"></el-alert>

            <div v-if="ficha">
                <el-row :gutter="10" class="u-mb-20">
                    <el-col :xs="24" :sm="8">
                        <div class="score-box" :class="ficha.classificacao_risco">
                            <span class="n">{{ ficha.score_percentual }}%</span>
                            <span class="r">RISCO {{ ficha.classificacao_risco.toUpperCase() }}</span>
                            <small>{{ ficha.configuracao.rotulo }} · {{ dataBr(ficha.data_referencia) }}</small>
                        </div>
                    </el-col>
                    <el-col :xs="24" :sm="16">
                        <div class="dados-aluno">
                            <h3>{{ ficha.matricula.aluno_nome }}</h3>
                            <p>
                                <span v-if="ficha.matricula.turma_nome">Turma {{ ficha.matricula.turma_nome }} · </span>
                                <span v-if="ficha.matricula.instituicao">{{ ficha.matricula.instituicao }}</span>
                                <span v-if="ficha.matricula.num_matricula"> · Matrícula {{ ficha.matricula.num_matricula }}</span>
                            </p>
                            <p v-if="ficha.modelo">
                                <small class="u-text-muted">
                                    Modelo {{ ficha.modelo.versao }}
                                    <span v-if="ficha.modelo.acuracia">· acurácia {{ pct(ficha.modelo.acuracia) }}</span>
                                    <span v-if="ficha.modelo.qtd_amostras">· {{ ficha.modelo.qtd_amostras }} amostras de treino</span>
                                </small>
                            </p>
                            <div class="u-mt-10">
                                <el-button size="small" icon="el-icon-printer" @click="imprimir">IMPRIMIR FICHA</el-button>
                                <el-button class="u-ml-10" v-if="podeIntervir" size="small" type="primary" plain icon="el-icon-first-aid-kit"
                                    @click="abrirIntervencao">ABRIR INTERVENÇÃO PEDAGÓGICA</el-button>
                            </div>
                        </div>
                    </el-col>
                </el-row>

                <!-- ==================== FATORES (explicabilidade) ==================== -->
                <h4 class="u-mb-10">Fatores que mais contribuíram</h4>
                <div class="container-table u-mb-20">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>Fator</th>
                                <th style="width: 120px;" class="u-text-center">Valor</th>
                                <th style="width: 140px;" class="u-text-center">Contribuição</th>
                                <th style="width: 140px;" class="u-text-center">Origem do dado</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(f, k) in ficha.fatores" :key="k">
                                <td>{{ f.rotulo }}</td>
                                <td class="u-text-center">{{ f.valor !== null ? numero(f.valor) : '—' }}</td>
                                <td class="u-text-center">{{ numero(f.contribuicao, 3) }}</td>
                                <td class="u-text-center">
                                    <el-tag size="mini" :type="f.dado_disponivel ? 'success' : 'info'">
                                        {{ f.dado_disponivel ? 'dado real' : 'valor neutro' }}
                                    </el-tag>
                                </td>
                            </tr>
                            <tr v-if="ficha.fatores.length === 0">
                                <td colspan="4" class="u-text-center">Nenhum fator elevou o risco deste aluno.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <small class="u-text-muted">
                    "Valor neutro" significa que a informação não existia no sistema no momento do
                    cálculo — o modelo não a considerou como evidência.
                </small>

                <!-- ==================== EVOLUÇÃO (item 137) ==================== -->
                <h4 class="u-mt-25 u-mb-10">Evolução do risco</h4>
                <div v-if="temSerie">
                    <line-chart :chart-data="dadosSerie" :height="280"></line-chart>
                </div>
                <el-alert v-else type="info" :closable="false"
                    title="Ainda não há pontos suficientes para mostrar a evolução."
                    description="A série se forma a cada execução do cálculo."></el-alert>

                <!-- ==================== TODAS AS FEATURES ==================== -->
                <h4 class="u-mt-25 u-mb-10">Todos os dados considerados</h4>
                <div class="container-table">
                    <table class="table table-striped">
                        <thead>
                            <tr><th>Dado</th><th style="width: 160px;" class="u-text-center">Valor</th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="(v, k) in ficha.features" :key="k">
                                <td>{{ ficha.catalogo_features[k] || k }}</td>
                                <td class="u-text-center">{{ numero(v) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <el-alert v-else-if="erro" type="warning" :closable="false" :title="erro"></el-alert>
            <div v-else class="u-text-center u-mt-25"><img src="/public/img/loading-bar.gif" /></div>
        </c-panel>
    </div>
</template>
<script>
import LineChart from '@/chart/LineChart.js'

/** BLOCO K — itens 135 e 137: ficha de risco com fatores e evolução. */
export default {
    components: { LineChart },
    data: () => ({ ficha: null, erro: null, configuracaoId: null, matriculaId: null }),
    computed: {
        podeIntervir () { return [0, 1, 3, 4, 5, 6, 7, 9, 12].includes(parseInt(this.$store.state.user.tipo)) },
        temSerie () {
            return !!(this.ficha && this.ficha.serie && this.ficha.serie.length
                && this.ficha.serie[0].labels && this.ficha.serie[0].labels.length > 1)
        },
        dadosSerie () {
            if (!this.temSerie) return { labels: [], datasets: [] }

            const s = this.ficha.serie[0]
            return {
                labels: s.labels.map(l => this.dataBr(l).substring(0, 5)),
                datasets: [{
                    label: 'Score de risco (%)',
                    borderColor: '#f56c6c',
                    backgroundColor: 'rgba(245, 108, 108, 0.15)',
                    fill: true,
                    data: s.valores.map(v => (v * 100).toFixed(1))
                }]
            }
        }
    },
    mounted () {
        this.configuracaoId = this.$route.query.configuracao
        this.matriculaId = this.$route.query.matricula

        if (!this.configuracaoId || !this.matriculaId) {
            this.erro = 'Abra a ficha a partir do painel de risco.'
            return
        }

        this.carregar()
    },
    methods: {
        carregar () {
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/preditivo/fichaAluno/${this.configuracaoId}/${this.matriculaId}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { this.erro = this.textoErro(r.data.erro); return }
                this.ficha = r.data
            }).catch(e => this.$store.commit('throwException', e))
        },
        /**
         * O atalho leva à tela de intervenções já existente do módulo pedagógico —
         * não se cria um segundo registro de acompanhamento aqui.
         */
        abrirIntervencao () {
            this.$router.push('/admin/pedagogico/intervencao')
        },
        imprimir () {
            window.eventBus.$emit('print', {
                url: `/api/preditivo/imprimirFicha/${this.configuracaoId}/${this.matriculaId}`,
                param: {}
            })
        },
        numero (v, casas) {
            if (v === null || v === undefined) return '—'
            return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas || 2, maximumFractionDigits: casas || 2 })
        },
        pct (v) { return v !== null && v !== undefined ? (parseFloat(v) * 100).toFixed(1) + '%' : '—' },
        dataBr (d) {
            if (!d) return '—'
            const p = d.substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        },
        textoErro (e) { return typeof e === 'string' ? e : (e.text || 'Não foi possível concluir.') }
    }
}
</script>
<style scoped>
.score-box { border: 2px solid #dcdfe6; border-radius: 6px; padding: 20px; text-align: center; }
.score-box .n { display: block; font-size: 42px; font-weight: bold; line-height: 1.1; }
.score-box .r { display: block; font-size: 14px; font-weight: bold; margin-top: 6px; }
.score-box small { display: block; color: #909399; margin-top: 6px; }
.score-box.alto { border-color: #f56c6c; } .score-box.alto .n, .score-box.alto .r { color: #c62828; }
.score-box.medio { border-color: #e6a23c; } .score-box.medio .n, .score-box.medio .r { color: #ef6c00; }
.score-box.baixo { border-color: #67c23a; } .score-box.baixo .n, .score-box.baixo .r { color: #2e7d32; }
.dados-aluno h3 { margin: 0 0 6px; }
.dados-aluno p { margin: 0 0 4px; color: #606266; }
</style>
